/**
 * Dialog for editing existing employee absences
 * Includes form validation, optimistic locking, and recurring series support
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLingui } from '@lingui/react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TimePicker } from '@/components/ui/time-picker';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { DatePicker } from '@/components/ui/date-picker';
import { AlertTriangle, Loader2, RefreshCw, Trash2, Repeat, Pencil } from 'lucide-react';
import { InlineConfirm } from '@/components/ui/confirm-dialog';
import { AbsenceTypeBadge } from './AbsenceTypeBadge';
import {
  AbsenceSeriesUpdateChoiceDialog,
  type AbsenceUpdateScope,
} from './AbsenceSeriesUpdateChoiceDialog';
import { DeleteAbsenceSeriesDialog } from './DeleteAbsenceSeriesDialog';
import { ApiError } from '@/api';
import { extractBackendError, translateBackendError } from '@/lib/translateBackendError';
import type { AbsenceTypeValue, UpdateRecurringAbsenceRequest } from '@/types/employeeAbsences';
import {
  useUpdateEmployeeAbsence,
  useDeleteEmployeeAbsence,
  useAbsencePattern,
  useUpdateRecurringAbsence,
  employeeAbsenceKeys,
} from '@/hooks/useEmployeeAbsences';
import { AbsenceType } from '@/types/employeeAbsences';
import type { EmployeeAbsence } from '@/types/employeeAbsences';
import {
  getUpdateAbsenceSchema,
  toUpdateAbsenceRequest,
  absenceToFormDefaults,
  type UpdateAbsenceFormData,

  type UpdateAbsenceFormInput,
} from '@/schemas/absenceSchemas';

/** Every type the office may set on an absence, in select order. */
const ALL_ABSENCE_TYPES: readonly AbsenceTypeValue[] = [
  AbsenceType.VACATION,
  AbsenceType.SICK,
  AbsenceType.PERSONAL,
  AbsenceType.TRAINING,
  AbsenceType.COMP_TIME,
  AbsenceType.ACCIDENT,
  AbsenceType.CHILD_SICK,
  AbsenceType.OTHER,
];

/** A self-report stays a report type (the backend refuses any other, D2). */
const REPORT_ABSENCE_TYPES: readonly AbsenceTypeValue[] = [
  AbsenceType.SICK,
  AbsenceType.ACCIDENT,
  AbsenceType.CHILD_SICK,
];

/**
 * Report types the office may pick for a report of `current` type. ACCIDENT
 * needs accident fields only the employee's report collects, so the backend
 * refuses turning another report into one (ACCIDENT_FIELDS_REQUIRED); it is
 * offered only when the report already is an accident.
 */
function reportTypesFor(current: string | undefined): readonly AbsenceTypeValue[] {
  return current === AbsenceType.ACCIDENT
    ? REPORT_ABSENCE_TYPES
    : REPORT_ABSENCE_TYPES.filter((type) => type !== AbsenceType.ACCIDENT);
}

/**
 * Report errors (#1767) shown in the dialog with their translated text. The
 * update hook skips its toast for these so the message shows only once.
 */
const REPORT_UPDATE_ERROR_CODES: ReadonlySet<string> = new Set([
  'ABSENCE_REPORT_CONFLICT',
  'ABSENCE_REPORT_NOT_ACTIVE',
  'ACCIDENT_FIELDS_REQUIRED',
]);

export interface EditAbsenceDialogProps {
  absence: EmployeeAbsence;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

/**
 * Dialog component for editing existing employee absences.
 * Handles optimistic locking via the currentUpdatedAt field.
 * Supports recurring series with scope choice dialog.
 */
export function EditAbsenceDialog({
  absence,
  open,
  onOpenChange,
  onSuccess,
}: EditAbsenceDialogProps) {
  const { i18n } = useLingui();
  const queryClient = useQueryClient();
  const updateAbsence = useUpdateEmployeeAbsence({
    inlineErrorCodes: REPORT_UPDATE_ERROR_CODES,
  });
  const deleteAbsence = useDeleteEmployeeAbsence();
  const updateRecurringAbsence = useUpdateRecurringAbsence();
  const [optimisticLockError, setOptimisticLockError] = useState(false);
  const [overlapError, setOverlapError] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteSeriesDialogOpen, setDeleteSeriesDialogOpen] = useState(false);
  const [showChoiceDialog, setShowChoiceDialog] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<UpdateAbsenceFormData | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);

  // A self-report (#1767, D27): report types only, full days only, no series,
  // no delete (the office cancels a report with a reason instead).
  const isReport = absence.is_self_report === true;
  const absenceTypes = isReport ? reportTypesFor(absence.absence_type) : ALL_ABSENCE_TYPES;

  // Series detection
  const isSeriesAbsence = !isReport && !!(absence.pattern_id && !absence.is_exception);

  // Fetch pattern data when editing a series absence
  const { data: patternData, isLoading: patternLoading } = useAbsencePattern(
    absence.pattern_id || '',
    { enabled: isSeriesAbsence && open }
  );

  // Form setup with Zod validation
  const schema = useMemo(
    () => getUpdateAbsenceSchema(i18n),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.locale, not the stable i18n instance
    [i18n.locale]
  );
  const form = useForm<UpdateAbsenceFormInput, unknown, UpdateAbsenceFormData>({
    resolver: zodResolver(schema),
    defaultValues: absenceToFormDefaults(absence),
  });

  // Watch fullDay to show/hide time fields
  const fullDay = useWatch({ control: form.control, name: 'fullDay' });

  // Reset form when dialog opens or absence changes
  useEffect(() => {
    if (open) {
      form.reset(absenceToFormDefaults(absence));
      setOptimisticLockError(false);
      setOverlapError(false);
      setDeleteDialogOpen(false);
      setDeleteSeriesDialogOpen(false);
      setShowChoiceDialog(false);
      setPendingFormData(null);
      setReportError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, absence]);

  // Handle update error (optimistic lock, overlap, etc.)
  const handleUpdateError = useCallback(
    (error: unknown) => {
      // A report changed or ended meanwhile (#1767, D21): show the code's own
      // text and refetch the list so the row shows the stored state.
      const code = extractBackendError(error).code;
      if (code && REPORT_UPDATE_ERROR_CODES.has(code)) {
        setReportError(
          translateBackendError(code, i18n, {
            id: 'absences.toast.error',
            message: 'Error performing absence operation',
          })
        );
        queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.lists() });
        if (absence.id) {
          queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.detail(absence.id) });
        }
        return;
      }
      // Check for 409 error
      if (error instanceof ApiError && error.status === 409) {
        const message = error.message?.toLowerCase() || '';
        if (
          message.includes('modified by another user') ||
          message.includes('optimistic')
        ) {
          setOptimisticLockError(true);
          return;
        }
        if (message.includes('overlap')) {
          setOverlapError(true);
          return;
        }
        // Default to optimistic lock for unknown 409 errors
        setOptimisticLockError(true);
        return;
      }
      // Fallback to message checking for non-ApiError cases
      const errorMessage =
        error instanceof Error ? error.message.toLowerCase() : '';
      if (
        errorMessage.includes('modified') ||
        errorMessage.includes('optimistic')
      ) {
        setOptimisticLockError(true);
      } else if (errorMessage.includes('overlap')) {
        setOverlapError(true);
      }
    },
    [i18n, queryClient, absence.id]
  );

  // Handle form submission
  const handleSubmit = async (data: UpdateAbsenceFormData) => {
    setOptimisticLockError(false);
    setOverlapError(false);
    setReportError(null);
    if (!absence.id) {
      throw new Error('EditAbsenceDialog: absence.id is missing');
    }

    // For series absences (non-exception), show the scope choice dialog
    if (isSeriesAbsence) {
      setPendingFormData(data);
      setShowChoiceDialog(true);
      return;
    }

    // For non-series absences or exception absences, update directly
    try {
      const request = toUpdateAbsenceRequest(data);
      await updateAbsence.mutateAsync({ id: absence.id, request });
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      handleUpdateError(error);
    }
  };

  // Handle series update with chosen scope
  const handleSeriesUpdate = useCallback(
    async (scope: AbsenceUpdateScope) => {
      if (!pendingFormData || !absence.id || !absence.pattern_id) {
        return;
      }

      try {
        if (scope === 'this_only') {
          // "This occurrence only" uses the single-absence PUT (marks as exception)
          const request = toUpdateAbsenceRequest(pendingFormData);
          await updateAbsence.mutateAsync({ id: absence.id, request });
        } else {
          // "all" or "future" uses the pattern PUT endpoint
          if (!patternData) {
            return;
          }

          const request: UpdateRecurringAbsenceRequest = {
            employee_id: patternData.employee_id || absence.employee?.id || '',
            absence_type: pendingFormData.absenceType as UpdateRecurringAbsenceRequest['absence_type'],
            full_day: pendingFormData.fullDay,
            from_time: !pendingFormData.fullDay ? pendingFormData.fromTime : undefined,
            to_time: !pendingFormData.fullDay ? pendingFormData.toTime : undefined,
            notes: pendingFormData.notes || undefined,
            recurrence_type: patternData.recurrence_type as UpdateRecurringAbsenceRequest['recurrence_type'],
            recurrence_days: patternData.recurrence_days || [],
            recurrence_interval: patternData.recurrence_interval || 1,
            start_date: patternData.start_date || format(pendingFormData.fromDate, 'yyyy-MM-dd'),
            end_date: patternData.end_date || undefined,
            scope: scope as UpdateRecurringAbsenceRequest['scope'],
          };

          await updateRecurringAbsence.mutateAsync({
            patternId: absence.pattern_id,
            request,
          });
        }

        setShowChoiceDialog(false);
        setPendingFormData(null);
        onOpenChange(false);
        onSuccess?.();
      } catch (error) {
        handleUpdateError(error);
        setShowChoiceDialog(false);
      }
    },
    [
      pendingFormData,
      absence.id,
      absence.pattern_id,
      absence.employee?.id,
      patternData,
      updateAbsence,
      updateRecurringAbsence,
      onOpenChange,
      onSuccess,
      handleUpdateError,
    ]
  );

  // Handle refresh after optimistic lock error
  const handleRefresh = () => {
    if (!absence.id) {
      return;
    }
    queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.detail(absence.id) });
    queryClient.invalidateQueries({ queryKey: employeeAbsenceKeys.lists() });
    onOpenChange(false);
  };

  // Handle delete action
  const handleDelete = async () => {
    if (!absence.id) {
      return;
    }
    // A failure rejects: the hook toasts and the inline confirm re-enables.
    await deleteAbsence.mutateAsync(absence.id);
    setDeleteDialogOpen(false);
    onOpenChange(false);
    onSuccess?.();
  };

  // Handle delete series success
  const handleDeleteSeriesSuccess = () => {
    setDeleteSeriesDialogOpen(false);
    onOpenChange(false);
    onSuccess?.();
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {i18n._({ id: 'absences.dialog.edit.title', message: 'Edit Absence' })}
          </DialogTitle>
          <DialogDescription className="sr-only">
            <Trans id="common.dialog.formDescription">Fill in the fields, then save.</Trans>
          </DialogDescription>
          {/* Show employee name (read-only) */}
          {absence.employee && (
            <div className="text-sm text-muted-foreground pt-1">
              {absence.employee.firstname} {absence.employee.lastname}
            </div>
          )}
          {/* Series and Exception Indicators */}
          {!isReport && (absence.pattern_id || absence.is_exception) && (
            <div className="flex flex-wrap gap-2 pt-2">
              {absence.pattern_id && (
                <Badge
                  variant="secondary"
                  className="flex items-center gap-1"
                  data-testid="series-badge"
                >
                  <Repeat className="h-3 w-3" />
                  {i18n._({
                    id: 'absences.series.partOfSeries',
                    message: 'Part of a series',
                  })}
                </Badge>
              )}
              {absence.is_exception && (
                <Badge
                  variant="outline"
                  className="flex items-center gap-1 border-warning-on-surface/40 bg-warning-muted text-warning-on-surface"
                  data-testid="exception-badge"
                >
                  <Pencil className="h-3 w-3" />
                  {i18n._({
                    id: 'absences.series.modified',
                    message: 'Individually modified',
                  })}
                </Badge>
              )}
            </div>
          )}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {/* Optimistic Lock Error */}
            {optimisticLockError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>
                  {i18n._({
                    id: 'absences.error.optimisticLock',
                    message: 'This absence was modified by another user. Please refresh.',
                  })}
                </AlertTitle>
                <AlertDescription className="flex items-center gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleRefresh}
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {i18n._({
                      id: 'absences.error.optimisticLock.refresh',
                      message: 'Refresh',
                    })}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            {/* Overlap Error */}
            {overlapError && !optimisticLockError && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>
                  {i18n._({
                    id: 'absences.error.overlap',
                    message: 'Overlap Detected',
                  })}
                </AlertTitle>
                <AlertDescription>
                  {i18n._({
                    id: 'absences.toast.overlapError',
                    message: 'Absence overlaps with existing absence',
                  })}
                </AlertDescription>
              </Alert>
            )}

            {/* Report changed or ended meanwhile (#1767) */}
            {reportError && (
              <Alert variant="destructive" data-testid="report-update-error">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{reportError}</AlertDescription>
              </Alert>
            )}

            {/* Absence Type */}
            <FormField
              control={form.control}
              name="absenceType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'absences.form.absenceType', message: 'Absence Type' })}
                  </FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {absenceTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          <div className="flex items-center gap-2">
                            <AbsenceTypeBadge type={type} />
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Date Fields */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="fromDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'absences.form.fromDate', message: 'From Date' })}
                    </FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={i18n._({
                          id: 'common.selectDate',
                          message: 'Select date',
                        })}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="toDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {i18n._({ id: 'absences.form.toDate', message: 'To Date' })}
                    </FormLabel>
                    <FormControl>
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={i18n._({
                          id: 'common.selectDate',
                          message: 'Select date',
                        })}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Full Day Toggle - a report is always full days */}
            {!isReport && (
              <FormField
                control={form.control}
                name="fullDay"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>
                        {i18n._({ id: 'absences.form.fullDay', message: 'Full Day' })}
                      </FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            {/* Time Fields - only shown when fullDay is false */}
            {!isReport && !fullDay && (
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="fromTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {i18n._({ id: 'absences.form.fromTime', message: 'From Time' })}
                      </FormLabel>
                      <FormControl>
                        <TimePicker value={field.value || ''} onChange={field.onChange} onBlur={field.onBlur} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="toTime"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {i18n._({ id: 'absences.form.toTime', message: 'To Time' })}
                      </FormLabel>
                      <FormControl>
                        <TimePicker value={field.value || ''} onChange={field.onChange} onBlur={field.onBlur} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Notes */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {i18n._({ id: 'absences.form.notes', message: 'Notes' })}
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} maxLength={500} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Hidden currentUpdatedAt field for optimistic locking */}
            <input type="hidden" {...form.register('currentUpdatedAt')} />

            {/* Action Buttons; the delete confirm takes their place (no stacked modal) */}
            {deleteDialogOpen ? (
              <InlineConfirm
                className="pt-4"
                message={i18n._({
                  id: 'absences.dialog.delete.confirmation',
                  message: 'Are you sure you want to delete this absence? This action cannot be undone.',
                })}
                confirmLabel={i18n._({ id: 'common.delete', message: 'Delete' })}
                onConfirm={handleDelete}
                onCancel={() => setDeleteDialogOpen(false)}
                isPending={deleteAbsence.isPending}
              />
            ) : (
              <div className="flex justify-between pt-4">
                <div className="flex gap-2">
                  {!isReport && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => setDeleteDialogOpen(true)}
                      disabled={updateAbsence.isPending || deleteAbsence.isPending}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {i18n._({ id: 'common.delete', message: 'Delete' })}
                    </Button>
                  )}
                  {!isReport && absence.pattern_id && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDeleteSeriesDialogOpen(true)}
                      disabled={updateAbsence.isPending || deleteAbsence.isPending}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {i18n._({
                        id: 'absences.series.deleteSeries',
                        message: 'Delete Series',
                      })}
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                  >
                    {i18n._({ id: 'common.cancel', message: 'Cancel' })}
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      updateAbsence.isPending ||
                      updateRecurringAbsence.isPending ||
                      optimisticLockError ||
                      (isSeriesAbsence && patternLoading)
                    }
                  >
                    {(updateAbsence.isPending || updateRecurringAbsence.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    )}
                    {i18n._({ id: 'common.save', message: 'Save' })}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>

    {/* Delete Series Dialog (never for a report) */}
    {!isReport && absence.pattern_id && (
      <DeleteAbsenceSeriesDialog
        patternId={absence.pattern_id}
        open={deleteSeriesDialogOpen}
        onOpenChange={setDeleteSeriesDialogOpen}
        onSuccess={handleDeleteSeriesSuccess}
      />
    )}

    {/* Series Update Choice Dialog */}
    <AbsenceSeriesUpdateChoiceDialog
      open={showChoiceDialog}
      onOpenChange={setShowChoiceDialog}
      onChoose={handleSeriesUpdate}
      isPending={updateAbsence.isPending || updateRecurringAbsence.isPending}
    />
    </>
  );
}
